import { useCallback, useState } from 'react';

import { useCreateUserPaymentMethod } from '@givebacks/api/core';
import { useQueryClient } from '@tanstack/react-query';
import { TokenEx, useBreakPoints, useFeatureFlag_Core, useFormListValidation } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate } from 'react-router-dom';
import { CardInfoFields } from 'store/Components';
import {
  ButtonMantine,
  ImageMantine,
  OneToTwoLayoutTemplate,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';
import { TokenExSampleCard } from 'ui/Icons';

import { AddCardProvider, useAddCardProvider } from './AddCard.context';

const AddCardButton = () => {
  const queryClient = useQueryClient();
  const { setError } = useAddCardProvider();
  const { validateForms } = useFormListValidation();
  const { getTokenizedCard } = TokenEx.useTokenize();
  const { mutate: createPaymentMethod, isPending: isLoading } = useCreateUserPaymentMethod();
  const navigate = useNavigate();
  const { isFeatureEnabled: isAccountPageEnabled } = useFeatureFlag_Core({
    featureId: 'user.account_page',
  });

  const handleClick = useCallback(async () => {
    const credential = await getTokenizedCard();
    const { isValid } = validateForms();

    if (isValid) {
      createPaymentMethod(credential, {
        onError(error) {
          setError(getErrorMessage_v2(error));
        },
        onSuccess() {
          notifications.show({
            message: 'Card added successfully',
            variant: 'success',
          });
          void queryClient.invalidateQueries({ queryKey: ['user', 'credit_cards'] });
          navigate(isAccountPageEnabled ? '/account' : '/wallet');
        },
      });
    }
  }, [
    createPaymentMethod,
    getTokenizedCard,
    navigate,
    setError,
    validateForms,
    isAccountPageEnabled,
    queryClient,
  ]);

  return (
    <Stack>
      <ButtonMantine fullWidth onClick={handleClick} loading={isLoading}>
        Add Card
      </ButtonMantine>
    </Stack>
  );
};

const TokenExCard = () => {
  return <ImageMantine src={TokenExSampleCard} />;
};

const LeftSection = () => {
  const { isSmallScreen } = useBreakPoints();
  return (
    <Stack gap={isSmallScreen ? 'md' : 'lg'}>
      <TokenExCard />
      {!isSmallScreen && <AddCardButton />}
    </Stack>
  );
};

const CardInfoSection = () => {
  const { error } = useAddCardProvider();
  return <CardInfoFields withTerms={true} error={error} withCvv={false} />;
};

const AddCard = () => {
  const [error, setError] = useState<string | undefined>();
  const { isSmallScreen } = useBreakPoints();

  return (
    <AddCardProvider value={{ error, setError }}>
      <OneToTwoLayoutTemplate
        leftSection={<LeftSection />}
        rightSection={
          <Stack>
            {!isSmallScreen && <Title order={2}>Add Card</Title>}
            <Text fz="sm" fw={600}>
              Debit Or Credit Card
            </Text>
            <CardInfoSection />
          </Stack>
        }
        mobileBottomButton={<AddCardButton />}
      />
    </AddCardProvider>
  );
};

export { AddCard };
